import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEventsV1Form from './events-v1-form-forms.http.js';

/**
 * The form defines which elements are displayed to a site visitor during the registration process (RSVP or checkout).
 * It also contains customizable messages and labels.
 *
 *
 * A form is an ordered list of controls (blocks), which accept guest information into a field input.
 *
 * Each control contains one or more nested inputs. For example, `Name` control has two inputs:
 * - First Name
 * - Last Name
 *
 * By default, name and email controls are always required and are pinned to the top of the form.
 */
export interface Form {
  /** Nested fields as an ordered list. */
  controls?: InputControl[];
  /**
   * Set of defined form messages displayed in the UI before, during, and after a registration flow.
   * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
   */
  messages?: FormMessages;
}

/**
 * A block of nested fields.
 * Used to aggregate similar inputs like First Name and Last Name.
 */
export interface InputControl {
  /** Field control type. */
  type?: InputControlTypeWithLiterals;
  /** Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed. */
  system?: boolean;
  /**
   * Deprecated: Use `id` or `_id`.
   * @deprecated
   */
  name?: string;
  /** Child inputs. */
  inputs?: Input[];
  /**
   * *Deprecated:** Use `controls.inputs.label`.
   * @deprecated
   */
  label?: string;
  /** Field controls are sorted by this value in ascending order. */
  orderIndex?: number;
  /** Unique control ID. */
  _id?: string;
  /**
   * Whether the input control is deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export enum InputControlType {
  /** Single text value field. */
  INPUT = 'INPUT',
  /** Single text value field with multiple lines. */
  TEXTAREA = 'TEXTAREA',
  /** Single-choice field with predefined values. */
  DROPDOWN = 'DROPDOWN',
  /** Single-choice field with predefined values. */
  RADIO = 'RADIO',
  /** Multiple-choice field with predefined values. */
  CHECKBOX = 'CHECKBOX',
  /** Fields for entering first and last names. */
  NAME = 'NAME',
  /** Fields for additional guests and their respective names. */
  GUEST_CONTROL = 'GUEST_CONTROL',
  /** Single-line address field. */
  ADDRESS_SHORT = 'ADDRESS_SHORT',
  /** Full address field with multiple lines. */
  ADDRESS_FULL = 'ADDRESS_FULL',
  /** Fields for entering year, month, and day. */
  DATE = 'DATE',
}

/** @enumType */
export type InputControlTypeWithLiterals =
  | InputControlType
  | 'INPUT'
  | 'TEXTAREA'
  | 'DROPDOWN'
  | 'RADIO'
  | 'CHECKBOX'
  | 'NAME'
  | 'GUEST_CONTROL'
  | 'ADDRESS_SHORT'
  | 'ADDRESS_FULL'
  | 'DATE';

/** Child inputs. */
export interface Input {
  /** Field name. */
  name?: string;
  /**
   * *Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.
   * @deprecated
   */
  array?: boolean;
  /** Main field label. */
  label?: string;
  /** Additional labels for multi-valued fields such as address. */
  additionalLabels?: Record<string, string>;
  /** Predefined choice options for fields, such as dropdown. */
  options?: string[];
  /** Whether field is mandatory. */
  mandatory?: boolean;
  /** Maximum number of accepted characters (relevant for text fields). */
  maxLength?: number;
  /**
   * Type which determines field format.
   * Used to validate submitted response.
   */
  type?: ValueTypeWithLiterals;
  /**
   * The maximum number of accepted values for array input.
   *
   * **Note:** Only applicable for `TEXT_ARRAY` input fields.
   */
  maxSize?: number | null;
  /**
   * Default option initially selected when an input has multiple choices.
   *
   * Defaults to first (0th) option, if not configured.
   * Currently only applicable for `type.dropdown`.
   */
  defaultOptionSelection?: OptionSelection;
  /**
   * Additional labels for multi-valued fields, such as address.
   * @readonly
   */
  labels?: Label[];
}

export enum ValueType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  TEXT_ARRAY = 'TEXT_ARRAY',
  DATE_TIME = 'DATE_TIME',
  ADDRESS = 'ADDRESS',
}

/** @enumType */
export type ValueTypeWithLiterals =
  | ValueType
  | 'TEXT'
  | 'NUMBER'
  | 'TEXT_ARRAY'
  | 'DATE_TIME'
  | 'ADDRESS';

/**
 * Describes initially selected option when an input has multiple choices.
 * Defaults to first (0th) option if not configured.
 */
export interface OptionSelection extends OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

/** @oneof */
export interface OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

export interface Label {
  /** Field name. */
  name?: string;
  /** Field label. */
  label?: string;
}

/**
 * Defines form messages shown in UI before, during, and after registration flow.
 * It enables configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
 */
export interface FormMessages {
  /** [RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages. */
  rsvp?: RsvpFormMessages;
  /** Checkout form messages. */
  checkout?: CheckoutFormMessages;
  /** Messages shown when event registration is closed. */
  registrationClosed?: RegistrationClosedMessages;
  /** Messages shown when event tickets are unavailable. */
  ticketsUnavailable?: TicketsUnavailableMessages;
}

export interface RsvpFormMessages {
  /** Label text indicating RSVP's `status` is `"YES"`. */
  rsvpYesOption?: string;
  /** Label text indicating RSVP's `status` is `"NO"`. */
  rsvpNoOption?: string;
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: Negative;
  /** "Submit form" call-to-action label text. */
  submitActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface PositiveResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarActionLabel?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface NegativeResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Set of messages shown during registration when RSVP response is positive. */
export interface Positive {
  /** Main form title for positive response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: PositiveResponseConfirmation;
}

/** A set of messages shown during registration with negative response */
export interface Negative {
  /** Main form title for negative response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: NegativeResponseConfirmation;
}

export interface CheckoutFormMessages {
  /** Main form title for response. */
  title?: string;
  /** Submit form call-to-action label text. */
  submitActionLabel?: string;
  /** Confirmation messages shown after checkout. */
  confirmation?: ResponseConfirmation;
}

/** Confirmation messages shown after checkout. */
export interface ResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Download tickets" call-to-action label text. */
  downloadTicketsLabel?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarLabel?: string;
  /** "Share event" call-to-action label text. */
  shareEventLabel?: string;
}

export interface RegistrationClosedMessages {
  /** Message shown when event registration is closed. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface TicketsUnavailableMessages {
  /** Message shown when event tickets are unavailable. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface FormInputControlAdded {
  /** Event ID. */
  eventId?: string;
  /** Input control. */
  inputControl?: InputControl;
}

export interface FormInputControlUpdated {
  /** Event ID. */
  eventId?: string;
  /** Input control. */
  updatedInputControl?: InputControl;
}

export interface FormInputControlDeleted {
  /** Event ID. */
  eventId?: string;
  /** Input control. */
  deletedInputControl?: InputControl;
}

export interface GetFormRequest {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
}

export enum RequestedFields {
  UNKNOWN_REQUESTED_FIELD = 'UNKNOWN_REQUESTED_FIELD',
  /** Include soft deleted input controls in the response. */
  DELETED = 'DELETED',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'UNKNOWN_REQUESTED_FIELD'
  | 'DELETED';

export interface GetFormResponse {
  /**
   * Event form.
   * Form is visible to site visitors.
   */
  form?: Form;
  /**
   * Deprecated, currently returns the same value as the form field.
   * @deprecated Deprecated, currently returns the same value as the form field.
   * @targetRemovalDate 2026-06-30
   */
  draftForm?: Form;
}

export interface AddControlRequest extends AddControlRequestControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Single-choice radio button style input control. */
  radioButton?: RadioButtonControl;
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
}

/** @oneof */
export interface AddControlRequestControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Single-choice radio button style input control. */
  radioButton?: RadioButtonControl;
}

export interface PhoneControl {
  /**
   * Phone input label.
   * @maxLength 100
   */
  label?: string;
  /** Whether the phone input is required. */
  mandatory?: boolean;
}

export interface AddressControl {
  /** Address control labels for each input. */
  labels?: AddressControlLabels;
  /** Whether an address is multi-line (consisting of multiple fields such as country, city, postal code). When `false`, address is single-line. */
  full?: boolean;
  /** Whether the address input is required. */
  mandatory?: boolean;
}

export interface AddressControlLabels {
  /**
   * Single-line address input label.
   * @maxLength 100
   */
  addressLine?: string;
  /**
   * Country input label.
   * @maxLength 100
   */
  country?: string;
  /**
   * Subdivision input label.
   * @maxLength 100
   */
  subdivision?: string;
  /**
   * City input label.
   * @maxLength 100
   */
  city?: string;
  /**
   * Postal code input label.
   * @maxLength 100
   */
  postalCode?: string;
  /**
   * Street address input label.
   * @maxLength 100
   */
  streetAddress?: string;
}

export interface DateControl {
  /**
   * Input control label.
   * @maxLength 100
   */
  label?: string;
  /** Whether the date input is required. */
  mandatory?: boolean;
}

export interface AdditionalGuestsControl {
  /** Additional guests control labels for each input. */
  labels?: Labels;
  /** Whether the individual guest names are required. */
  namesMandatory?: boolean;
  /**
   * Maximum number of additional guests.
   * @min 1
   * @max 10
   */
  maxGuests?: number;
}

export interface Labels {
  /**
   * Input label for a single guest.
   * @maxLength 200
   */
  single?: string;
  /**
   * Input label for multiple guests.
   * @maxLength 200
   */
  multiple?: string;
}

export interface DropdownControl {
  /**
   * Input control label.
   * @maxLength 200
   */
  label?: string;
  /**
   * Predefined options guests can choose from.
   * @minSize 2
   * @maxSize 200
   * @maxLength 200
   */
  options?: string[];
  /** Whether a choice is required. */
  mandatory?: boolean;
  /**
   * Default option initially selected when an input has multiple choices.
   *
   * Defaults to first (0th) option, if not configured.
   */
  defaultOptionSelection?: OptionSelection;
}

export interface RadioButtonControl {
  /**
   * Input control label.
   * @maxLength 200
   */
  label?: string;
  /**
   * Predefined options guests can choose from.
   * @minSize 2
   * @maxSize 200
   * @maxLength 200
   */
  options?: string[];
}

export interface CheckboxControl {
  /**
   * Input control label.
   * @maxLength 200
   */
  label?: string;
  /** Whether at least one checkbox is required. */
  mandatory?: boolean;
  /**
   * Predefined options guests can choose from.
   * @minSize 1
   * @maxSize 200
   * @maxLength 200
   */
  options?: string[];
}

export interface TextControl {
  /**
   * Input control label.
   * @maxLength 200
   */
  label?: string;
  /** Whether a text input is required. */
  mandatory?: boolean;
  /**
   * Maximum number of characters allowed.
   * @max 1000
   */
  maxLength?: number;
  /** Whether the input control should allow multiple lines in text. */
  multiLine?: boolean;
  /** Whether the input control should be displayed as a comment. */
  comment?: boolean;
}

export interface AddControlResponse {
  /** Generated unique input control ID. */
  _id?: string;
  /** Modified event form. */
  form?: Form;
}

export interface UpdateControlRequest extends UpdateControlRequestControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Main guest name input control. */
  name?: NameControl;
  /** Main guest email input control. */
  email?: EmailControl;
  /** Single-choice radio style input control. */
  radioButton?: RadioButtonControl;
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
  /** Unique input control ID. */
  _id: string;
  /** Index used to sort input controls in ascending order. */
  orderIndex?: number;
}

/** @oneof */
export interface UpdateControlRequestControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Main guest name input control. */
  name?: NameControl;
  /** Main guest email input control. */
  email?: EmailControl;
  /** Single-choice radio style input control. */
  radioButton?: RadioButtonControl;
}

export interface NameControl {
  /** Name control labels of each input. */
  labels?: NameControlLabels;
}

export interface NameControlLabels {
  /**
   * First name input label.
   * @maxLength 100
   */
  firstName?: string;
  /**
   * Last name input label .
   * @maxLength 100
   */
  lastName?: string;
}

export interface EmailControl {
  /**
   * Email input label.
   * @maxLength 100
   */
  label?: string;
}

export interface UpdateControlResponse {
  /** Modified event form. */
  form?: Form;
}

export interface DeleteControlRequest {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique input control ID.
   * @maxLength 100
   */
  _id: string;
}

export interface DeleteControlResponse {
  /** Modified draft event form. */
  form?: Form;
}

export interface UpdateMessagesRequest {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Set of field paths, specifying which parts of this resource to update.
   * When fields are empty, request is interpreted as full update.
   * Behavior follows [google.protobuf.FieldMask](https://developers.google.com/protocol-buffers/docs/reference/google.protobuf#google.protobuf.FieldMask) semantics.
   */
  fields?: string[];
  /** Set of configured form messages to update. */
  messages?: FormMessages;
}

export interface UpdateMessagesResponse {
  /** Modified event form. */
  form?: Form;
}

export interface PublishDraftRequest {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
}

export interface PublishDraftResponse {
  /** Event form. */
  form?: Form;
}

export interface EventUpdated {
  /** Event update timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: Location;
  /** Event schedule configuration. */
  scheduleConfig?: ScheduleConfig;
  /** Event title. */
  title?: string;
  /**
   * Whether schedule configuration was updated.
   * @deprecated
   */
  scheduleConfigUpdated?: boolean;
  /** Updated event */
  event?: Event;
}

export interface Location {
  /**
   * Location name.
   * @maxLength 50
   */
  name?: string | null;
  /** Location map coordinates. */
  coordinates?: MapCoordinates;
  /**
   * Single line address representation.
   * @maxLength 300
   */
  address?: string | null;
  /** Location type. */
  type?: LocationTypeWithLiterals;
  /**
   * Full address derived from formatted single line `address`.
   * When `full_address` is used to create or update the event, deprecated `address` and `coordinates` are ignored.
   * If provided `full_address` has empty `formatted_address` or `coordinates`, it will be auto-completed using Atlas service.
   *
   * Migration notes:
   * - `full_address.formatted_address` is equivalent to `address`.
   * - `full_address.geocode` is equivalent to `coordinates`.
   */
  fullAddress?: Address;
  /**
   * Defines event location as TBD (To Be Determined).
   * When event location is not yet defined, `name` is displayed instead of location address.
   * `coordinates`, `address`, `type` and `full_address` are not required when location is TBD.
   */
  tbd?: boolean | null;
}

export interface MapCoordinates {
  /**
   * Latitude.
   * @min -90
   * @max 90
   */
  lat?: number;
  /**
   * Longitude.
   * @min -180
   * @max 180
   */
  lng?: number;
}

export enum LocationType {
  VENUE = 'VENUE',
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationTypeWithLiterals = LocationType | 'VENUE' | 'ONLINE';

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine1?: string | null;
  /**
   * country code
   * @format COUNTRY
   */
  country?: string | null;
  /** subdivision (usually state or region) code according to ISO 3166-2 */
  subdivision?: string | null;
  /** city name */
  city?: string | null;
  /** zip/postal code */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, Floor */
  addressLine2?: string | null;
  /** A string containing the human-readable address of this location */
  formatted?: string | null;
  /** Free text for human-to-human textual orientation aid purposes */
  hint?: string | null;
  /** coordinates of the physical address */
  location?: AddressLocation;
  /** country full-name */
  countryFullname?: string | null;
  /**
   * multi-level subdivisions from top to bottom
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** street number */
  number?: string;
  /** street name */
  name?: string;
}

export interface AddressLocation {
  /**
   * address latitude coordinates
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * address longitude coordinates
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface Subdivision {
  /** subdivision short code */
  code?: string;
  /** subdivision full-name */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface ScheduleConfig {
  /**
   * Defines event as TBD (To Be Determined) schedule.
   * When event time is not yet defined, TBD message is displayed instead of event start and end times.
   * `startDate`, `endDate` and `timeZoneId` are not required when schedule is TBD.
   */
  scheduleTbd?: boolean;
  /**
   * TBD message.
   * @maxLength 100
   */
  scheduleTbdMessage?: string | null;
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether end date is hidden in the formatted schedule. */
  endDateHidden?: boolean;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
  /** Event recurrences. */
  recurrences?: Recurrences;
}

export interface Recurrences {
  /**
   * Event occurrences.
   * @maxSize 1000
   */
  occurrences?: Occurrence[];
  /**
   * Recurring event category ID.
   * @readonly
   */
  categoryId?: string | null;
  /**
   * Recurrence status.
   * @readonly
   */
  status?: StatusWithLiterals;
}

export interface Occurrence {
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
}

export enum Status {
  /** Event occurs only once. */
  ONE_TIME = 'ONE_TIME',
  /** Event is recurring. */
  RECURRING = 'RECURRING',
  /** Marks the next upcoming occurrence of the recurring event. */
  RECURRING_NEXT = 'RECURRING_NEXT',
  /** Marks the most recent ended occurrence of the recurring event. */
  RECURRING_LAST_ENDED = 'RECURRING_LAST_ENDED',
  /** Marks the most recent canceled occurrence of the recurring event. */
  RECURRING_LAST_CANCELED = 'RECURRING_LAST_CANCELED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_NEXT'
  | 'RECURRING_LAST_ENDED'
  | 'RECURRING_LAST_CANCELED';

export interface Event {
  /**
   * Event ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /** Event location. */
  location?: Location;
  /** Event scheduling. */
  scheduling?: Scheduling;
  /** Event title. */
  title?: string;
  /** Event description. */
  description?: string;
  /** Rich-text content that are displayed in a site's "About Event" section (HTML). */
  about?: string;
  /** Main event image. */
  mainImage?: string;
  /** Event slug URL (generated from event title). */
  slug?: string;
  /** ISO 639-1 language code of the event (used in content translations). */
  language?: string;
  /** Event creation timestamp. */
  created?: Date | null;
  /** Event modified timestamp. */
  modified?: Date | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /** RSVP or ticketing registration details. */
  registration?: Registration;
  /** "Add to calendar" URLs. */
  calendarLinks?: CalendarLinks;
  /** Event page URL components. */
  eventPageUrl?: SiteUrl;
  /** Event registration form. */
  form?: Form;
  /** Event dashboard summary of RSVP / ticket sales. */
  dashboard?: Dashboard;
  /** Instance ID of the site where event is hosted. */
  instanceId?: string;
  /** Guest list configuration. */
  guestListConfig?: GuestListConfig;
  /**
   * Event creator user ID.
   * @maxLength 36
   */
  userId?: string;
  /** Event discussion feed. For internal use. */
  feed?: Feed;
  /** Online conferencing details. */
  onlineConferencing?: OnlineConferencing;
  /** SEO settings. */
  seoSettings?: SeoSettings;
  /** Assigned contacts label key. */
  assignedContactsLabel?: string | null;
  /** Agenda details. */
  agenda?: Agenda;
  /** Categories this event is assigned to. */
  categories?: Category[];
  /** Visual settings for event. */
  eventDisplaySettings?: EventDisplaySettings;
  /** Rich content that are displayed in a site's "About Event" section. Successor to `about` field. */
  longDescription?: RichContent;
  /**
   * Event publish timestamp.
   * @readonly
   */
  publishedDate?: Date | null;
}

export interface Scheduling {
  /** Schedule configuration. */
  config?: ScheduleConfig;
  /** Formatted schedule representation. */
  formatted?: string;
  /** Formatted start date of the event (empty for TBD schedules). */
  startDateFormatted?: string;
  /** Formatted start time of the event (empty for TBD schedules). */
  startTimeFormatted?: string;
  /** Formatted end date of the event (empty for TBD schedules or when end date is hidden). */
  endDateFormatted?: string;
  /** Formatted end time of the event (empty for TBD schedules or when end date is hidden). */
  endTimeFormatted?: string;
}

export enum EventStatus {
  /** Event is public and scheduled to start */
  SCHEDULED = 'SCHEDULED',
  /** Event has started */
  STARTED = 'STARTED',
  /** Event has ended */
  ENDED = 'ENDED',
  /** Event was canceled */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type EventStatusWithLiterals =
  | EventStatus
  | 'SCHEDULED'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED';

export interface Registration {
  /** Event type. */
  type?: EventTypeWithLiterals;
  /** Event registration status. */
  status?: RegistrationStatusWithLiterals;
  /** RSVP collection details. */
  rsvpCollection?: RsvpCollection;
  /** Ticketing details. */
  ticketing?: Ticketing;
  /** External registration details. */
  external?: ExternalEvent;
  /** Types of users allowed to register. */
  restrictedTo?: VisitorTypeWithLiterals;
  /** Initial event type which was set when creating an event. */
  initialType?: EventTypeWithLiterals;
}

export enum EventType {
  /** Type not available for this request fieldset */
  NA_EVENT_TYPE = 'NA_EVENT_TYPE',
  /** Registration via RSVP */
  RSVP = 'RSVP',
  /** Registration via ticket purchase */
  TICKETS = 'TICKETS',
  /** External registration */
  EXTERNAL = 'EXTERNAL',
  /** Registration not available */
  NO_REGISTRATION = 'NO_REGISTRATION',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'NA_EVENT_TYPE'
  | 'RSVP'
  | 'TICKETS'
  | 'EXTERNAL'
  | 'NO_REGISTRATION';

export enum RegistrationStatus {
  /** Registration status is not applicable */
  NA_REGISTRATION_STATUS = 'NA_REGISTRATION_STATUS',
  /** Registration to event is closed */
  CLOSED = 'CLOSED',
  /** Registration to event is closed manually */
  CLOSED_MANUALLY = 'CLOSED_MANUALLY',
  /** Registration is open via RSVP */
  OPEN_RSVP = 'OPEN_RSVP',
  /** Registration to event waitlist is open via RSVP */
  OPEN_RSVP_WAITLIST = 'OPEN_RSVP_WAITLIST',
  /** Registration is open via ticket purchase */
  OPEN_TICKETS = 'OPEN_TICKETS',
  /** Registration is open via external URL */
  OPEN_EXTERNAL = 'OPEN_EXTERNAL',
  /** Registration will be open via RSVP */
  SCHEDULED_RSVP = 'SCHEDULED_RSVP',
}

/** @enumType */
export type RegistrationStatusWithLiterals =
  | RegistrationStatus
  | 'NA_REGISTRATION_STATUS'
  | 'CLOSED'
  | 'CLOSED_MANUALLY'
  | 'OPEN_RSVP'
  | 'OPEN_RSVP_WAITLIST'
  | 'OPEN_TICKETS'
  | 'OPEN_EXTERNAL'
  | 'SCHEDULED_RSVP';

export interface RsvpCollection {
  /** RSVP collection configuration. */
  config?: RsvpCollectionConfig;
}

export interface RsvpCollectionConfig {
  /** Defines the supported RSVP statuses. */
  rsvpStatusOptions?: RsvpStatusOptionsWithLiterals;
  /**
   * Total guest limit available to register to the event.
   * Additional guests per RSVP are counted towards total guests.
   */
  limit?: number | null;
  /** Whether a waitlist is opened when total guest limit is reached, allowing guests to create RSVP with WAITING RSVP status. */
  waitlist?: boolean;
  /** Registration start timestamp. */
  startDate?: Date | null;
  /** Registration end timestamp. */
  endDate?: Date | null;
}

export enum RsvpStatusOptions {
  /** Only YES RSVP status is available for RSVP registration */
  YES_ONLY = 'YES_ONLY',
  /** YES and NO RSVP status options are available for the registration */
  YES_AND_NO = 'YES_AND_NO',
}

/** @enumType */
export type RsvpStatusOptionsWithLiterals =
  | RsvpStatusOptions
  | 'YES_ONLY'
  | 'YES_AND_NO';

export interface RsvpConfirmationMessages {
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveConfirmation?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: RsvpConfirmationMessagesNegativeResponseConfirmation;
}

/** Confirmation shown after then registration when RSVP response is positive. */
export interface RsvpConfirmationMessagesPositiveResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

/** Confirmation shown after then registration when RSVP response is negative. */
export interface RsvpConfirmationMessagesNegativeResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export interface Ticketing {
  /**
   * Deprecated.
   * @deprecated
   */
  lowestPrice?: string | null;
  /**
   * Deprecated.
   * @deprecated
   */
  highestPrice?: string | null;
  /** Currency used in event transactions. */
  currency?: string | null;
  /** Ticketing configuration. */
  config?: TicketingConfig;
  /**
   * Price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPrice?: Money;
  /**
   * Price of highest priced ticket.
   * @readonly
   */
  highestTicketPrice?: Money;
  /**
   * Formatted price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPriceFormatted?: string | null;
  /**
   * Formatted price of highest priced ticket.
   * @readonly
   */
  highestTicketPriceFormatted?: string | null;
  /**
   * Whether all tickets are sold for this event.
   * @readonly
   */
  soldOut?: boolean | null;
}

export interface TicketingConfig {
  /** Whether the form must be filled out separately for each ticket. */
  guestAssignedTickets?: boolean;
  /** Tax configuration. */
  taxConfig?: TaxConfig;
  /**
   * Limit of tickets that can be purchased per order, default 20.
   * @max 50
   */
  ticketLimitPerOrder?: number;
  /**
   * Duration for which the tickets being bought are reserved.
   * @min 5
   * @max 30
   */
  reservationDurationInMinutes?: number | null;
}

export interface TaxConfig {
  /** Tax application settings. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @minLength 1
   * @maxLength 10
   */
  name?: string | null;
  /**
   * Tax rate (e.g.,`21.55`).
   * @decimalValue options { gte:0.001, lte:100, maxScale:3 }
   */
  rate?: string | null;
  /** Applies taxes for donations, default true. */
  appliesToDonations?: boolean | null;
}

export enum TaxType {
  /** Tax is included in the ticket price. */
  INCLUDED = 'INCLUDED',
  /** Tax is added to the order at the checkout. */
  ADDED = 'ADDED',
  /** Tax is added to the final total at the checkout. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED'
  | 'ADDED'
  | 'ADDED_AT_CHECKOUT';

export interface TicketsConfirmationMessages {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Download tickets" call-to-action label text.
   * @maxLength 50
   */
  downloadTicketsLabel?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export enum CheckoutType {
  UNKNOWN_CHECKOUT_TYPE = 'UNKNOWN_CHECKOUT_TYPE',
  /** Checkout using Events App. */
  EVENTS_APP = 'EVENTS_APP',
  /** Checkout using Ecomm Platform. */
  ECOMM_PLATFORM = 'ECOMM_PLATFORM',
}

/** @enumType */
export type CheckoutTypeWithLiterals =
  | CheckoutType
  | 'UNKNOWN_CHECKOUT_TYPE'
  | 'EVENTS_APP'
  | 'ECOMM_PLATFORM';

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface ExternalEvent {
  /** External event registration URL. */
  registration?: string;
}

export enum VisitorType {
  /** Site visitor (including member) */
  VISITOR = 'VISITOR',
  /** Site member */
  MEMBER = 'MEMBER',
  /** Site visitor or member */
  VISITOR_OR_MEMBER = 'VISITOR_OR_MEMBER',
}

/** @enumType */
export type VisitorTypeWithLiterals =
  | VisitorType
  | 'VISITOR'
  | 'MEMBER'
  | 'VISITOR_OR_MEMBER';

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

/** Site URL components */
export interface SiteUrl {
  /**
   * Base URL. For premium sites, this will be the domain.
   * For free sites, this would be site URL (e.g `mysite.wixsite.com/mysite`)
   */
  base?: string;
  /** The path to that page - e.g `/my-events/weekly-meetup-2` */
  path?: string;
}

export interface Dashboard {
  /** Guest RSVP summary. */
  rsvpSummary?: RsvpSummary;
  /**
   * Summary of revenue and tickets sold.
   * (Archived orders are not included).
   */
  ticketingSummary?: TicketingSummary;
}

export interface RsvpSummary {
  /** Total number of RSVPs. */
  total?: number;
  /** Number of RSVPs with status `YES`. */
  yes?: number;
  /** Number of RSVPs with status `NO`. */
  no?: number;
  /** Number of RSVPs in waitlist. */
  waitlist?: number;
}

export interface TicketingSummary {
  /** Number of tickets sold. */
  tickets?: number;
  /**
   * Total revenue, excluding fees.
   * (taxes and payment provider fees are not deducted.)
   */
  revenue?: Money;
  /** Whether currency is locked and cannot be changed (generally occurs after the first order in the specified currency has been created). */
  currencyLocked?: boolean;
  /** Number of orders placed. */
  orders?: number;
  /** Total balance of confirmed transactions. */
  totalSales?: Money;
}

export interface GuestListConfig {
  /** Whether members can see other members attending the event (defaults to true). */
  publicGuestList?: boolean;
}

export interface Feed {
  /** Event discussion feed token. */
  token?: string;
}

export interface OnlineConferencing {
  config?: OnlineConferencingConfig;
  session?: OnlineConferencingSession;
}

export interface OnlineConferencingConfig {
  /**
   * Whether online conferencing is enabled (not supported for TBD schedules).
   * When enabled, links to join conferencing are generated and provided to guests.
   */
  enabled?: boolean;
  /**
   * Conferencing provider ID.
   * @format GUID
   */
  providerId?: string | null;
  /** Conference type */
  conferenceType?: ConferenceTypeWithLiterals;
}

export enum ConferenceType {
  /** Everyone in the meeting can publish and subscribe video and audio. */
  MEETING = 'MEETING',
  /** Guests can only subscribe to video and audio. */
  WEBINAR = 'WEBINAR',
}

/** @enumType */
export type ConferenceTypeWithLiterals = ConferenceType | 'MEETING' | 'WEBINAR';

export interface OnlineConferencingSession {
  /**
   * Link for event host to start the online conference session.
   * @readonly
   */
  hostLink?: string;
  /**
   * Link for guests to join the online conference session.
   * @readonly
   */
  guestLink?: string;
  /**
   * The password required to join online conferencing session (when relevant).
   * @readonly
   */
  password?: string | null;
  /**
   * Indicates that session was created successfully on providers side.
   * @readonly
   */
  sessionCreated?: boolean | null;
  /**
   * Unique session id
   * @readonly
   */
  sessionId?: string | null;
}

export interface SeoSettings {
  /**
   * URL slug
   * @maxLength 130
   */
  slug?: string;
  /** Advanced SEO data */
  advancedSeoData?: SeoSchema;
  /**
   * Hidden from SEO Site Map
   * @readonly
   */
  hidden?: boolean | null;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface Agenda {
  /** Whether the schedule is enabled for the event. */
  enabled?: boolean;
  /**
   * Agenda page URL.
   * @readonly
   */
  pageUrl?: SiteUrl;
}

/**
 * A Category is a classification object that groups related events on a site so you can organize and display them by themes, venues, or other facets.
 *
 * You can manage Categories, assign events to them, and use them to control the selection and order of events across different pages and widgets.
 *
 * Read more about [Categories](https://support.wix.com/en/article/creating-and-displaying-event-categories).
 */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Category name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string;
  /**
   * Date and time when category was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * The total number of draft and published events assigned to the category.
   * @readonly
   */
  counts?: CategoryCounts;
  /**
   * Category state.
   *
   * Default: `MANUAL`.
   *
   * **Note:** The WIX_EVENTS.MANAGE_AUTO_CATEGORIES permission scope is required to use states other than `MANUAL`.
   * @maxSize 3
   */
  states?: StateWithLiterals[];
}

export interface CategoryCounts {
  /** Total number of draft events assigned to the category. */
  assignedEventsCount?: number | null;
  /** Total number of published events assigned to the category. Deleted events are excluded. */
  assignedDraftEventsCount?: number | null;
}

export enum State {
  /** Categoty is created manually by the user. */
  MANUAL = 'MANUAL',
  /** Category is created automatically. */
  AUTO = 'AUTO',
  /** Category is created automatically when publishing recurring events. */
  RECURRING_EVENT = 'RECURRING_EVENT',
  /** Category is hidden. */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'MANUAL'
  | 'AUTO'
  | 'RECURRING_EVENT'
  | 'HIDDEN';

export interface EventDisplaySettings {
  /** Whether event details button is hidden. Only available for events with no registration. */
  hideEventDetailsButton?: boolean | null;
  /** Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page. */
  hideEventDetailsPage?: boolean | null;
}

export interface LabellingSettings {}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
}

/** @enumType */
export type SourceWithLiterals = Source | 'HTML' | 'ADSENSE' | 'AI';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum BackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals =
  | BackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface Gradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface Background extends BackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: Gradient;
  /** Background type. For each option, include the relevant details. */
  type?: BackgroundTypeWithLiterals;
}

/** @oneof */
export interface BackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: Gradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: Background;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: BackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border */
  borderRadius?: number | null;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backdropColor?: string | null;
  /** Backdrop image.radius in pixels. */
  backdropImage?: BackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface Badge {
  /** Badge type. */
  type?: TypeWithLiterals;
  /**
   * Badge text.
   * @maxLength 50
   */
  text?: string | null;
}

export enum Type {
  /** Unknown badge type. */
  UNKNOWN_BADGE_TYPE = 'UNKNOWN_BADGE_TYPE',
  /** 1st priority badge type. */
  FIRST_PRIORITY = 'FIRST_PRIORITY',
  /** 2nd priority badge type. */
  SECOND_PRIORITY = 'SECOND_PRIORITY',
  /** 3rd priority badge type. */
  THIRD_PRIORITY = 'THIRD_PRIORITY',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN_BADGE_TYPE'
  | 'FIRST_PRIORITY'
  | 'SECOND_PRIORITY'
  | 'THIRD_PRIORITY';

export interface DiscardDraftRequest {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
}

export interface DiscardDraftResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type AddControlApplicationErrors = {
  code?: 'INVALID_EVENT_CONFIGURATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateControlApplicationErrors =
  | {
      code?: 'INVALID_EVENT_CONFIGURATION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CONTROL_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DeleteControlApplicationErrors = {
  code?: 'CONTROL_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface FormEventUpdatedEnvelope {
  data: EventUpdated;
  metadata: BaseEventMetadata;
}

/**
 * Triggered when a form is updated or published.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Events
 * @permissionScopeId SCOPE.DC-EVENTS.READ-EVENTS
 * @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionScope View SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.VIEW-SEO
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-EVENTS
 * @permissionId WIX_EVENTS.READ_EVENTS
 * @webhook
 * @eventType wix.events.events.EventUpdated
 * @serviceIdentifier wix.events.form.FormBuilder
 * @slug event_updated
 */
export declare function onFormEventUpdated(
  handler: (event: FormEventUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves an event registration form.
 * @param eventId - Event ID to which the form belongs.
 * @public
 * @requiredField eventId
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_EVENTS
 * @applicableIdentity APP
 * @returns Event form.
 * Form is visible to site visitors.
 * @fqn wix.events.form.FormBuilder.GetForm
 */
export async function getForm(
  eventId: string
): Promise<
  NonNullablePaths<
    Form,
    | `controls`
    | `controls.${number}.type`
    | `controls.${number}.system`
    | `controls.${number}.name`
    | `controls.${number}.label`
    | `controls.${number}.orderIndex`
    | `controls.${number}._id`
    | `messages.rsvp.rsvpYesOption`
    | `messages.rsvp.rsvpNoOption`
    | `messages.rsvp.positiveMessages.title`
    | `messages.rsvp.positiveMessages.confirmation.title`
    | `messages.rsvp.positiveMessages.confirmation.message`
    | `messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `messages.rsvp.negativeMessages.title`
    | `messages.rsvp.negativeMessages.confirmation.title`
    | `messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `messages.rsvp.submitActionLabel`
    | `messages.checkout.title`
    | `messages.checkout.submitActionLabel`
    | `messages.checkout.confirmation.title`
    | `messages.checkout.confirmation.message`
    | `messages.checkout.confirmation.downloadTicketsLabel`
    | `messages.checkout.confirmation.addToCalendarLabel`
    | `messages.checkout.confirmation.shareEventLabel`
    | `messages.registrationClosed.message`
    | `messages.registrationClosed.exploreEventsActionLabel`
    | `messages.ticketsUnavailable.message`
    | `messages.ticketsUnavailable.exploreEventsActionLabel`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ eventId: eventId });

  const reqOpts = ambassadorWixEventsV1Form.getForm(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.form!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['eventId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds an input control to the form. The applied changes trigger form publishing.
 * @param eventId - Event ID to which the form belongs.
 * @public
 * @requiredField eventId
 * @requiredField options
 * @requiredField options.control
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.AddControl
 */
export async function addControl(
  eventId: string,
  options: AddControlOptions
): Promise<
  NonNullablePaths<
    AddControlResponse,
    | `_id`
    | `form.controls`
    | `form.controls.${number}.type`
    | `form.controls.${number}.system`
    | `form.controls.${number}.name`
    | `form.controls.${number}.label`
    | `form.controls.${number}.orderIndex`
    | `form.controls.${number}._id`
    | `form.messages.rsvp.rsvpYesOption`
    | `form.messages.rsvp.rsvpNoOption`
    | `form.messages.rsvp.positiveMessages.title`
    | `form.messages.rsvp.positiveMessages.confirmation.title`
    | `form.messages.rsvp.positiveMessages.confirmation.message`
    | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.negativeMessages.title`
    | `form.messages.rsvp.negativeMessages.confirmation.title`
    | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.submitActionLabel`
    | `form.messages.checkout.title`
    | `form.messages.checkout.submitActionLabel`
    | `form.messages.checkout.confirmation.title`
    | `form.messages.checkout.confirmation.message`
    | `form.messages.checkout.confirmation.downloadTicketsLabel`
    | `form.messages.checkout.confirmation.addToCalendarLabel`
    | `form.messages.checkout.confirmation.shareEventLabel`
    | `form.messages.registrationClosed.message`
    | `form.messages.registrationClosed.exploreEventsActionLabel`
    | `form.messages.ticketsUnavailable.message`
    | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
    7
  > & {
    __applicationErrorsType?: AddControlApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    phone: options?.phone,
    address: options?.address,
    date: options?.date,
    additionalGuests: options?.additionalGuests,
    dropdown: options?.dropdown,
    checkbox: options?.checkbox,
    text: options?.text,
    radioButton: options?.radioButton,
  });

  const reqOpts = ambassadorWixEventsV1Form.addControl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          phone: '$[1].phone',
          address: '$[1].address',
          date: '$[1].date',
          additionalGuests: '$[1].additionalGuests',
          dropdown: '$[1].dropdown',
          checkbox: '$[1].checkbox',
          text: '$[1].text',
          radioButton: '$[1].radioButton',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddControlOptions extends AddControlOptionsControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Single-choice radio button style input control. */
  radioButton?: RadioButtonControl;
}

/** @oneof */
export interface AddControlOptionsControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Single-choice radio button style input control. */
  radioButton?: RadioButtonControl;
}

/**
 * Updates an existing input control in the form. The applied changes trigger form publishing.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers._id
 * @requiredField identifiers.eventId
 * @requiredField options.control
 * @param options - Optional fields.
 * @param identifiers - Identifies what form to update.
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.UpdateControl
 */
export async function updateControl(
  identifiers: NonNullablePaths<UpdateControlIdentifiers, `_id` | `eventId`, 2>,
  options?: UpdateControlOptions
): Promise<
  NonNullablePaths<
    UpdateControlResponse,
    | `form.controls`
    | `form.controls.${number}.type`
    | `form.controls.${number}.system`
    | `form.controls.${number}.name`
    | `form.controls.${number}.label`
    | `form.controls.${number}.orderIndex`
    | `form.controls.${number}._id`
    | `form.messages.rsvp.rsvpYesOption`
    | `form.messages.rsvp.rsvpNoOption`
    | `form.messages.rsvp.positiveMessages.title`
    | `form.messages.rsvp.positiveMessages.confirmation.title`
    | `form.messages.rsvp.positiveMessages.confirmation.message`
    | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.negativeMessages.title`
    | `form.messages.rsvp.negativeMessages.confirmation.title`
    | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.submitActionLabel`
    | `form.messages.checkout.title`
    | `form.messages.checkout.submitActionLabel`
    | `form.messages.checkout.confirmation.title`
    | `form.messages.checkout.confirmation.message`
    | `form.messages.checkout.confirmation.downloadTicketsLabel`
    | `form.messages.checkout.confirmation.addToCalendarLabel`
    | `form.messages.checkout.confirmation.shareEventLabel`
    | `form.messages.registrationClosed.message`
    | `form.messages.registrationClosed.exploreEventsActionLabel`
    | `form.messages.ticketsUnavailable.message`
    | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
    7
  > & {
    __applicationErrorsType?: UpdateControlApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: identifiers?.eventId,
    id: identifiers?._id,
    orderIndex: options?.orderIndex,
    phone: options?.phone,
    address: options?.address,
    date: options?.date,
    additionalGuests: options?.additionalGuests,
    dropdown: options?.dropdown,
    checkbox: options?.checkbox,
    text: options?.text,
    name: options?.name,
    email: options?.email,
    radioButton: options?.radioButton,
  });

  const reqOpts = ambassadorWixEventsV1Form.updateControl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0].eventId',
          id: '$[0]._id',
          orderIndex: '$[1].orderIndex',
          phone: '$[1].phone',
          address: '$[1].address',
          date: '$[1].date',
          additionalGuests: '$[1].additionalGuests',
          dropdown: '$[1].dropdown',
          checkbox: '$[1].checkbox',
          text: '$[1].text',
          name: '$[1].name',
          email: '$[1].email',
          radioButton: '$[1].radioButton',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateControlIdentifiers
  extends UpdateControlIdentifiersControlOneOf {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
  /** Unique input control ID. */
  _id: string;
}

/** @oneof */
export interface UpdateControlIdentifiersControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Main guest name input control. */
  name?: NameControl;
  /** Main guest email input control. */
  email?: EmailControl;
  /** Single-choice radio style input control. */
  radioButton?: RadioButtonControl;
}

export interface UpdateControlOptions extends UpdateControlOptionsControlOneOf {
  /** Index used to sort input controls in ascending order. */
  orderIndex?: number;
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Main guest name input control. */
  name?: NameControl;
  /** Main guest email input control. */
  email?: EmailControl;
  /** Single-choice radio style input control. */
  radioButton?: RadioButtonControl;
}

/** @oneof */
export interface UpdateControlOptionsControlOneOf {
  /** Phone number input control. */
  phone?: PhoneControl;
  /** Single-line or full address input control. */
  address?: AddressControl;
  /** Day, month, year date input control. */
  date?: DateControl;
  /** Additional guests input control. */
  additionalGuests?: AdditionalGuestsControl;
  /** Single-choice dropdown style input control. */
  dropdown?: DropdownControl;
  /** Multiple-choice checkbox style input control. */
  checkbox?: CheckboxControl;
  /** Free-form text input control. */
  text?: TextControl;
  /** Main guest name input control. */
  name?: NameControl;
  /** Main guest email input control. */
  email?: EmailControl;
  /** Single-choice radio style input control. */
  radioButton?: RadioButtonControl;
}

/**
 * Deletes an input control from the form. The applied changes trigger form publishing.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers._id
 * @requiredField identifiers.eventId
 * @param identifiers - Identifies what form to delete.
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.DeleteControl
 */
export async function deleteControl(
  identifiers: NonNullablePaths<DeleteControlIdentifiers, `_id` | `eventId`, 2>
): Promise<
  NonNullablePaths<
    DeleteControlResponse,
    | `form.controls`
    | `form.controls.${number}.type`
    | `form.controls.${number}.system`
    | `form.controls.${number}.name`
    | `form.controls.${number}.label`
    | `form.controls.${number}.orderIndex`
    | `form.controls.${number}._id`
    | `form.messages.rsvp.rsvpYesOption`
    | `form.messages.rsvp.rsvpNoOption`
    | `form.messages.rsvp.positiveMessages.title`
    | `form.messages.rsvp.positiveMessages.confirmation.title`
    | `form.messages.rsvp.positiveMessages.confirmation.message`
    | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.negativeMessages.title`
    | `form.messages.rsvp.negativeMessages.confirmation.title`
    | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.submitActionLabel`
    | `form.messages.checkout.title`
    | `form.messages.checkout.submitActionLabel`
    | `form.messages.checkout.confirmation.title`
    | `form.messages.checkout.confirmation.message`
    | `form.messages.checkout.confirmation.downloadTicketsLabel`
    | `form.messages.checkout.confirmation.addToCalendarLabel`
    | `form.messages.checkout.confirmation.shareEventLabel`
    | `form.messages.registrationClosed.message`
    | `form.messages.registrationClosed.exploreEventsActionLabel`
    | `form.messages.ticketsUnavailable.message`
    | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
    7
  > & {
    __applicationErrorsType?: DeleteControlApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: identifiers?.eventId,
    id: identifiers?._id,
  });

  const reqOpts = ambassadorWixEventsV1Form.deleteControl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0].eventId', id: '$[0]._id' },
        singleArgumentUnchanged: false,
      },
      ['identifiers']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteControlIdentifiers {
  /**
   * Event ID to which the form belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * Unique input control ID.
   * @maxLength 100
   */
  _id: string;
}

/**
 * Updates a set of defined form messages that are displayed to a site visitor before, during, and after the registration flow.
 * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
 * @param eventId - Event ID to which the form belongs.
 * @public
 * @requiredField eventId
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.UpdateMessages
 */
export async function updateMessages(
  eventId: string,
  options?: UpdateMessagesOptions
): Promise<
  NonNullablePaths<
    UpdateMessagesResponse,
    | `form.controls`
    | `form.controls.${number}.type`
    | `form.controls.${number}.system`
    | `form.controls.${number}.name`
    | `form.controls.${number}.label`
    | `form.controls.${number}.orderIndex`
    | `form.controls.${number}._id`
    | `form.messages.rsvp.rsvpYesOption`
    | `form.messages.rsvp.rsvpNoOption`
    | `form.messages.rsvp.positiveMessages.title`
    | `form.messages.rsvp.positiveMessages.confirmation.title`
    | `form.messages.rsvp.positiveMessages.confirmation.message`
    | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.negativeMessages.title`
    | `form.messages.rsvp.negativeMessages.confirmation.title`
    | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.submitActionLabel`
    | `form.messages.checkout.title`
    | `form.messages.checkout.submitActionLabel`
    | `form.messages.checkout.confirmation.title`
    | `form.messages.checkout.confirmation.message`
    | `form.messages.checkout.confirmation.downloadTicketsLabel`
    | `form.messages.checkout.confirmation.addToCalendarLabel`
    | `form.messages.checkout.confirmation.shareEventLabel`
    | `form.messages.registrationClosed.message`
    | `form.messages.registrationClosed.exploreEventsActionLabel`
    | `form.messages.ticketsUnavailable.message`
    | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    fields: options?.fields,
    messages: options?.messages,
  });

  const reqOpts = ambassadorWixEventsV1Form.updateMessages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          fields: '$[1].fields',
          messages: '$[1].messages',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateMessagesOptions {
  /**
   * Set of field paths, specifying which parts of this resource to update.
   * When fields are empty, request is interpreted as full update.
   * Behavior follows [google.protobuf.FieldMask](https://developers.google.com/protocol-buffers/docs/reference/google.protobuf#google.protobuf.FieldMask) semantics.
   */
  fields?: string[];
  /** Set of configured form messages to update. */
  messages?: FormMessages;
}

/**
 * Publishes the draft form.
 * @param eventId - Event ID to which the form belongs.
 * @public
 * @requiredField eventId
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.PublishDraft
 * @deprecated
 * @targetRemovalDate 2026-01-05
 */
export async function publishDraft(
  eventId: string
): Promise<
  NonNullablePaths<
    PublishDraftResponse,
    | `form.controls`
    | `form.controls.${number}.type`
    | `form.controls.${number}.system`
    | `form.controls.${number}.name`
    | `form.controls.${number}.label`
    | `form.controls.${number}.orderIndex`
    | `form.controls.${number}._id`
    | `form.messages.rsvp.rsvpYesOption`
    | `form.messages.rsvp.rsvpNoOption`
    | `form.messages.rsvp.positiveMessages.title`
    | `form.messages.rsvp.positiveMessages.confirmation.title`
    | `form.messages.rsvp.positiveMessages.confirmation.message`
    | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
    | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.negativeMessages.title`
    | `form.messages.rsvp.negativeMessages.confirmation.title`
    | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
    | `form.messages.rsvp.submitActionLabel`
    | `form.messages.checkout.title`
    | `form.messages.checkout.submitActionLabel`
    | `form.messages.checkout.confirmation.title`
    | `form.messages.checkout.confirmation.message`
    | `form.messages.checkout.confirmation.downloadTicketsLabel`
    | `form.messages.checkout.confirmation.addToCalendarLabel`
    | `form.messages.checkout.confirmation.shareEventLabel`
    | `form.messages.registrationClosed.message`
    | `form.messages.registrationClosed.exploreEventsActionLabel`
    | `form.messages.ticketsUnavailable.message`
    | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ eventId: eventId });

  const reqOpts = ambassadorWixEventsV1Form.publishDraft(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['eventId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Clears all changes to the draft form.
 * @param eventId - Event ID to which the form belongs.
 * @public
 * @requiredField eventId
 * @permissionId WIX_EVENTS.MANAGE_EVENTS
 * @applicableIdentity APP
 * @fqn wix.events.form.FormBuilder.DiscardDraft
 * @deprecated
 * @targetRemovalDate 2026-01-05
 */
export async function discardDraft(eventId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ eventId: eventId });

  const reqOpts = ambassadorWixEventsV1Form.discardDraft(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['eventId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
